import type { ModelAssetNode } from '../assets/model-asset-types'
import type {
  AssemblyDocument,
  AssemblyPersistedChildNode,
  AssemblyPersistedChildTransformSnapshot,
  AssemblyTransform,
} from '../assembly-document/assembly-document-types'
import { isAssemblyPersistedDocument } from '../assembly-document/assembly-document-operations'
import {
  assemblyTransformFromMatrix,
  invertAssemblyTransform,
  multiplyAssemblyMatrixByTransform,
  multiplyAssemblyTransforms,
} from '../assembly-document/assembly-transform-matrix'
import {
  createAssemblyTransformSnapshotPath,
  normalizeAssemblyTransformSnapshotPath,
} from '../assembly-document/assembly-transform-snapshot-path'

/**
 * 加载时恢复装配位姿覆盖的输入。
 */
export interface ApplyAssemblyTransformOverridesInput {
  /**
   * 当前项目模型根节点。
   */
  readonly root: ModelAssetNode
  /**
   * 以模型节点 key 索引的 `_assembly.json` 文档。
   */
  readonly assemblyDocumentsByNodeKey: ReadonlyMap<string, AssemblyDocument>
}

/**
 * 4x4 单位矩阵，作为 direct child assembly 子树内部的相对根位姿。
 */
const IDENTITY_TRANSFORM: AssemblyTransform = [
  1, 0, 0, 0,
  0, 1, 0, 0,
  0, 0, 1, 0,
  0, 0, 0, 1,
]

/**
 * 复制装配位姿数组，避免恢复后的模型节点继续共享持久化文档或临时计算结果的数组引用。
 *
 * @param transform 需要复制的 4x4 row-major 装配位姿。
 * @returns 新的 16 位装配位姿数组。
 */
function cloneAssemblyTransform(transform: readonly number[]): AssemblyTransform {
  if (transform.length !== 16) {
    throw new Error(`assembly transform invalid: expected 16 numbers, received ${transform.length}`)
  }

  return [...transform] as unknown as AssemblyTransform
}

/**
 * 使用新的 children 克隆模型节点，避免加载恢复过程改写输入树。
 *
 * @param node 需要克隆的模型节点。
 * @param children 已恢复完成的子节点列表。
 * @returns 带有新 children 引用的模型节点。
 */
function cloneNodeWithChildren(node: ModelAssetNode, children: readonly ModelAssetNode[]): ModelAssetNode {
  return {
    ...node,
    children,
  }
}

/**
 * 使用新的 localTransform 克隆模型节点。
 *
 * @param node 需要克隆的模型节点。
 * @param localTransform 要写入的局部位姿。
 * @returns 带有新局部位姿的模型节点。
 */
function cloneNodeWithLocalTransform(node: ModelAssetNode, localTransform: AssemblyTransform): ModelAssetNode {
  return {
    ...node,
    localTransform: cloneAssemblyTransform(localTransform),
  }
}

/**
 * 读取模型节点 key 的最后一段稳定结构标识。
 *
 * @param nodeKey 模型节点 key。
 * @returns 最后一段非空 key；空 key 时返回空字符串。
 */
function nodeKeyTail(nodeKey: string): string {
  const segments = nodeKey.split('/').filter((segment) => segment.length > 0)

  return segments[segments.length - 1] ?? ''
}

/**
 * 判断缺失 instanceUuid 的节点是否可以安全使用同级索引兜底。
 *
 * @param candidate 同级索引命中的持久化子节点。
 * @param child 当前模型树直接子节点。
 * @returns 类型和至少一个稳定身份都匹配时返回 true。
 */
function isIndexFallbackCandidateMatch(candidate: AssemblyPersistedChildNode, child: ModelAssetNode): boolean {
  if (candidate.node_type !== child.nodeType) {
    return false
  }

  const tail = nodeKeyTail(child.nodeKey)

  return candidate.node_id === tail
    || candidate.instance_id === tail
    || (child.displayName !== undefined && candidate.instance_name === child.displayName)
}

/**
 * 定位当前模型直接子节点对应的持久化直接子节点。
 *
 * 设计说明：加载阶段需要尽量还原可用状态，但有 instanceUuid 的节点不能在实例未命中时退回索引，
 * 否则 sibling 重排或局部缺失会把错误位姿写到当前节点。只有旧数据缺少 instanceUuid 时才允许带校验的索引兜底。
 *
 * @param children 当前装配文档中的直接子节点记录。
 * @param child 当前模型树直接子节点。
 * @param childIndex 当前模型树直接子节点索引。
 * @returns 匹配到的持久化子节点；找不到时返回 null。
 */
function findPersistedDirectChild(
  children: readonly AssemblyPersistedChildNode[],
  child: ModelAssetNode,
  childIndex: number,
): AssemblyPersistedChildNode | null {
  if (child.instanceUuid) {
    const matchedByInstance = children.find((persistedChild) => persistedChild.instance_id === child.instanceUuid)

    if (matchedByInstance) {
      return matchedByInstance
    }

    return null
  }

  const fallbackCandidate = children[childIndex]

  return fallbackCandidate && isIndexFallbackCandidateMatch(fallbackCandidate, child)
    ? fallbackCandidate
    : null
}

/**
 * 将叶子零件相对 direct child 根的快照位姿换算成相对当前父节点的局部位姿。
 *
 * @param parentRelativeTransform 当前父节点相对 direct child 根的位姿。
 * @param snapshotTransform 叶子零件相对 direct child 根的快照位姿。
 * @returns 叶子零件应写回模型树的局部位姿。
 */
function resolveLocalTransformFromSnapshot(
  parentRelativeTransform: AssemblyTransform,
  snapshotTransform: AssemblyTransform,
): AssemblyTransform {
  return assemblyTransformFromMatrix(
    multiplyAssemblyMatrixByTransform(invertAssemblyTransform(parentRelativeTransform), snapshotTransform),
  )
}

/**
 * 构建快照路径索引。
 *
 * @param snapshots 持久化在 direct child assembly 上的叶子零件位姿快照。
 * @returns 规范化路径到快照位姿的索引。
 */
function createSnapshotTransformMap(
  snapshots: readonly AssemblyPersistedChildTransformSnapshot[],
): ReadonlyMap<string, AssemblyTransform> {
  return new Map(
    snapshots.map((snapshot) => [
      normalizeAssemblyTransformSnapshotPath(snapshot.path),
      snapshot.transform,
    ]),
  )
}

/**
 * 在 direct child assembly 子树内应用父装配保存的叶子零件快照。
 *
 * 设计说明：`children_transform_list` 是父装配在直接子装配边界上保存的恢复快照，因此它只覆盖该子装配内部的叶子零件，
 * 不改变子装配根自身位姿，也不继续把更底层子装配文档的结果作为最终权威。
 *
 * @param directChild 父装配的直接子装配节点。
 * @param snapshots 父装配写在该直接子装配上的叶子零件快照。
 * @returns 应用快照后的 direct child 子树。
 */
function applyDirectChildSnapshots(
  directChild: ModelAssetNode,
  snapshots: readonly AssemblyPersistedChildTransformSnapshot[],
): ModelAssetNode {
  const snapshotsByPath = createSnapshotTransformMap(snapshots)

  /**
   * 递归恢复 direct child 子树内部叶子位姿。
   *
   * @param node 当前子树节点。
   * @param parentRelativeTransform 当前父节点相对 direct child 根的位姿。
   * @returns 应用快照后的节点。
   */
  function visitSnapshotNode(node: ModelAssetNode, parentRelativeTransform: AssemblyTransform): ModelAssetNode {
    if (node.nodeType === 'part') {
      const snapshotPath = normalizeAssemblyTransformSnapshotPath(
        createAssemblyTransformSnapshotPath(directChild, node),
      )
      const snapshotTransform = snapshotsByPath.get(snapshotPath)

      if (!snapshotTransform) {
        return node
      }

      return cloneNodeWithLocalTransform(
        node,
        resolveLocalTransformFromSnapshot(parentRelativeTransform, snapshotTransform),
      )
    }

    const currentRelativeTransform = multiplyAssemblyTransforms(parentRelativeTransform, node.localTransform)
    const nextChildren = node.children.map((child) => visitSnapshotNode(child, currentRelativeTransform))

    return cloneNodeWithChildren(node, nextChildren)
  }

  const nextChildren = directChild.children.map((child) => visitSnapshotNode(child, IDENTITY_TRANSFORM))

  return cloneNodeWithChildren(directChild, nextChildren)
}

/**
 * 按 `_assembly.json` 在加载完成的模型树上恢复装配子节点位姿。
 *
 * 设计说明：恢复顺序必须先让子装配自己的文档恢复默认 direct child 位姿，再应用父装配写在 direct child assembly
 * 边界上的快照，这样父装配提交时记录的叶子位姿才能优先于更底层装配自己的历史记录。
 *
 * @param input 恢复输入。
 * @returns 新的模型节点树；不会原地修改 input.root。
 */
export function applyAssemblyTransformOverrides(input: ApplyAssemblyTransformOverridesInput): ModelAssetNode {
  /**
   * 递归访问模型树节点并按当前节点的装配文档恢复直接子节点。
   *
   * @param node 当前模型树节点。
   * @returns 恢复后的模型树节点。
   */
  function visitNode(node: ModelAssetNode): ModelAssetNode {
    const document = input.assemblyDocumentsByNodeKey.get(node.nodeKey)

    if (!isAssemblyPersistedDocument(document)) {
      return cloneNodeWithChildren(node, node.children.map(visitNode))
    }

    const nextChildren = node.children.map((child, childIndex) => {
      /**
       * V6 `project-model.json` 物化出的 occurrence 已从同包 `assembly.json` 取得权威矩阵。
       * `_assembly.json` 仍负责后端普通节点的采纳版本与历史位姿，但不能再次覆盖包内 occurrence，
       * 否则前端提交的新移动/旋转会在刷新时恢复成旧工作文档姿态。
       */
      const persistedChild = child.assetSource?.kind === 'embedded-puppy-occurrence'
        ? null
        : findPersistedDirectChild(document.children, child, childIndex)
      const childWithDirectTransform = persistedChild
        ? cloneNodeWithLocalTransform(child, persistedChild.transform)
        : child
      const childWithNestedDefaults = visitNode(childWithDirectTransform)

      if (
        childWithNestedDefaults.nodeType !== 'assembly'
        || !persistedChild?.children_transform_list
        || persistedChild.children_transform_list.length === 0
      ) {
        return childWithNestedDefaults
      }

      return applyDirectChildSnapshots(childWithNestedDefaults, persistedChild.children_transform_list)
    })

    return cloneNodeWithChildren(node, nextChildren)
  }

  return visitNode(input.root)
}
